import { useDisclosure, useHotkeys } from "@mantine/hooks";
import { useNavigate } from "@tanstack/react-router";
import { useEmbedMode } from "../utils/embed-detection";
import { useOverrides } from "./use-overrides";

export const useDevTools = () => {
  const [opened, { close, toggle }] = useDisclosure(false);
  const navigate = useNavigate();
  const isEmbedMode = useEmbedMode();
  const overrides = useOverrides();

  useHotkeys([["mod+I", () => toggle()]]);

  const handleToggleEmbedMode = () => {
    const currentPath = window.location.pathname;

    if (isEmbedMode) {
      // Remove embed parameter to go to regular mode
      navigate({
        to: currentPath,
        search: { embed: undefined }, // This will remove the embed parameter
      });
    } else {
      // Add embed parameter to go to embed mode
      navigate({
        to: currentPath,
        search: { embed: true }, // Use boolean true instead of string
      });
    }

    close();
  };

  const handleClearAppData = () => {
    // Clear overrides first (before clearing all localStorage)
    overrides.clearAllOverrides();

    // Clear localStorage
    try {
      localStorage.clear();
    } catch {
      // Silently continue if localStorage clearing fails
    }

    // Clear sessionStorage
    try {
      sessionStorage.clear();
    } catch {
      // Silently continue if sessionStorage clearing fails
    }

    // Clear IndexedDB databases
    try {
      if ("indexedDB" in window) {
        indexedDB.databases().then((databases) => {
          for (const db of databases.filter((db): db is IDBDatabaseInfo & { name: string } =>
            Boolean(db.name),
          )) {
            indexedDB.deleteDatabase(db.name);
          }
        });
      }
    } catch {
      // Silently continue if IndexedDB clearing fails
    }

    // Clear all caches
    try {
      if ("caches" in window) {
        caches.keys().then((cacheNames) => {
          for (const cacheName of cacheNames) {
            caches.delete(cacheName);
          }
        });
      }
    } catch {
      // Silently continue if cache clearing fails
    }

    // Clear cookies for current domain
    try {
      for (const cookie of document.cookie.split(";")) {
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        // biome-ignore lint/suspicious/noDocumentCookie: Dev tools need to clear cookies for development purposes
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      }
    } catch {
      // Silently continue if cookie clearing fails
    }

    // Always reload the page after clearing operations
    setTimeout(() => {
      window.location.reload();
    }, 100); // Small delay to ensure any pending operations complete
  };

  return {
    opened,
    close,
    toggle,
    handleToggleEmbedMode,
    handleClearAppData,
    isEmbedMode,
    overrides,
  };
};
